import { useMemo } from 'react';
import {
  type ResponseData,
  useApiQuery,
  useTeam,
  useTeamIssueCounts,
  useTeamPermission,
} from '@vrplatform/data';

export type IssueRow = {
  id: string;
  date: string | null;
  type: 'connection-error' | 'connection-version-outdated' | 'bank-feed-error';
  table: 'connection';
  level: 'error';
  message: string;
  item: React.ReactNode;
  itemName: string;
};

type IssuesData = { aggregate: number; list: IssueRow[] };

type TeamIssue =
  ResponseData<'get:/teams/issues'>['data'][number]['issues'][number];

const CONNECTION_ISSUE_CODES = [
  'brokenConnections',
  'outdatedConnections',
] satisfies TeamIssue['code'][];

export const useTeamIssues = (): IssuesData => {
  const [{ id: teamId }] = useTeam();
  const issueCounts = useTeamIssueCounts();
  const canReadTeamIssues = useTeamPermission('team-issues:read');

  const connectionIssueCount = useMemo(
    () =>
      issueCounts.reduce(
        (sum, issue) =>
          CONNECTION_ISSUE_CODES.some((code) => code === issue.code)
            ? sum + issue.count
            : sum,
        0
      ),
    [issueCounts]
  );

  // `/me.issues` only carries counts for the startup badges; the detailed
  // connection context loads on demand when issues exist.
  const { data } = useApiQuery(
    async (api, signal, args) => {
      const res = await api.GET('/teams/issues', {
        params: {
          query: {
            teamIds: args.teamId,
            codes: CONNECTION_ISSUE_CODES,
          },
        },
        signal,
      });

      if (res.error) throw res.error;

      return {
        issues: res.data.data.flatMap((team) => team.issues),
      };
    },
    {
      skip: !teamId || !canReadTeamIssues || connectionIssueCount === 0,
      variables: { teamId },
      queryGroup: 'teams',
      uniqueQueryKey: 'team-connection-issues',
      hideNotification: true,
      keepPreviousData: true,
    }
  );

  const issues = connectionIssueCount === 0 ? undefined : data?.issues;

  return useMemo<IssuesData>(() => {
    const list = (issues ?? []).reduce<IssueRow[]>((acc, issue: TeamIssue) => {
      const isBroken = issue.code === 'brokenConnections';
      const isOutdated = issue.code === 'outdatedConnections';

      if (!isBroken && !isOutdated) return acc;

      const type: IssueRow['type'] = isBroken
        ? 'connection-error'
        : 'connection-version-outdated';

      const message = isOutdated
        ? 'Please reconnect this connection to update the version.'
        : 'Connection error';

      const connections =
        issue.context && 'connections' in issue.context
          ? issue.context.connections
          : [];

      return connections.reduce<IssueRow[]>((inner, connection) => {
        const id = connection.id;
        const name = connection.name;
        const health = 'health' in connection ? connection.health : undefined;

        // Bank feeds (Plaid) report their own health; only issues that need
        // the user's action block data processing. Unknown or warning health
        // stays on the bank account indicator.
        if (health && health.status !== 'actionRequired') return inner;

        inner.push({
          id,
          type: health ? 'bank-feed-error' : type,
          table: 'connection',
          level: 'error',
          message:
            health?.issues.find((issue) => issue.severity === 'error')
              ?.message ?? message,
          date: null,
          itemName: name,
          item: name,
        });

        return inner;
      }, acc);
    }, []);

    return {
      // Raw counts include bank feeds that are filtered out above, so they
      // only stand in while the details load.
      aggregate: issues ? list.length : connectionIssueCount,
      list,
    };
  }, [issues, connectionIssueCount]);
};
